<template>
    <div>
        <div class="content-header row">
            <div class="content-header-left col-md-6 col-12 mb-2">
                <h3 class="content-header-title mb-0">B2B Gift Cards</h3>
            </div>
            <div class="content-header-right col-md-6 col-12">
                <Link :href="route('admin.pgc-giftcards.create')" class="btn btn-primary float-right">Generate Cards</Link>
            </div>
        </div>

        <div class="content-body">
            <section class="card">
                <div class="card-header">
                    <h4>B2B Gift Card Series</h4>
                </div>

                <div class="card-body">
                    <form>
                        <div class="row">
                            <div class="col-md-4">
                                <div class="form-group">
                                    <label for="series-search">Search by series or code</label>
                                    <input
                                        id="series-search"
                                        v-model="search"
                                        class="form-control form-control-sm"
                                        type="text"
                                        placeholder="Series name or card code"
                                    >
                                </div>
                            </div>
                            <div class="col-md-12 mb-2">
                                <button type="submit" class="btn btn-primary glow mb-1 mb-sm-0" @click.prevent="applySearch">
                                    Search
                                </button>
                                <button type="reset" class="btn btn-warning glow mb-1 mb-sm-0" @click.prevent="resetFilters">
                                    Reset
                                </button>
                            </div>
                        </div>
                    </form>

                    <vue-good-table
                        class="dataTable"
                        mode="remote"
                        :isLoading.sync="tableDataLoading"
                        :totalRows="totalRecords"
                        :pagination-options="paginationOptions"
                        :sort-options="sortingOptions"
                        @on-page-change="this.onPageChange"
                        @on-per-page-change="this.onPerPageChange"
                        styleClass="vgt-table"
                        :columns="columns"
                        :rows="allSeries"
                    >
                        <template slot="table-row" slot-scope="props">
                            <span v-if="props.column.field === 'sn'">
                                {{ serialNo(props) }}
                            </span>
                            <span v-else-if="props.column.field === 'total_value'">
                                ₦{{ Number(props.row.total_value).toLocaleString() }}
                            </span>
                            <span v-else-if="props.column.field === 'created_at'">
                                {{ formatDateTime(props.row.created_at) }}
                            </span>
                            <span v-else-if="props.column.field === 'expires_at'">
                                {{ formatExpiryDateTime(props.row.expires_at) }}
                            </span>
                            <table-row-actions v-else-if="props.column.field === 'action'">
                                <Link :href="route('admin.pgc-giftcards.show', props.row.series_slug)" class="dropdown-item">
                                    <i class="feather icon-eye"></i>
                                    View
                                </Link>
                                <a
                                    href="#"
                                    class="dropdown-item text-danger"
                                    @click.prevent="confirmDeleteSeries(props.row.series_slug)"
                                >
                                    <i class="feather icon-trash-2"></i>
                                    Delete
                                </a>
                            </table-row-actions>
                        </template>
                    </vue-good-table>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout';
import { Link } from '@inertiajs/vue2';
import DeleteAlert from '../../Shared/Mixins/DeleteAlert';
import PaginationMixin from '../../Shared/Mixins/PaginationMixin';
import { formatDateTime, formatExpiryDateTime } from '../../utils';

export default {
    layout: Layout,
    components: { Link },
    mixins: [DeleteAlert, PaginationMixin],
    props: ['series'],
    data() {
        return {
            search: '',
            allSeries: [],
            totalRecords: 0,
            serverParams: {
                page: 1,
                perPage: 10,
            },
            paginationOptions: {
                enabled: true,
                mode: 'pages',
                perPage: 10,
                perPageDropdown: [5, 10, 20, 50, 100, 200],
                dropdownAllowAll: false,
            },
            sortingOptions: {
                enabled: false,
            },
            columns: [
                { label: 'S/N', field: 'sn', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Series', field: 'series_name', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Cards', field: 'total_cards', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Total Value', field: 'total_value', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Created', field: 'created_at', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Expires', field: 'expires_at', thClass: 'text-center', tdClass: 'text-center' },
                { label: 'Action', field: 'action', sortable: false },
            ],
            tableDataLoading: false,
        };
    },
    mounted() {
        this.allSeries = this.series?.data ?? [];
        this.totalRecords = this.series?.total ?? 0;
    },
    methods: {
        formatDateTime,
        formatExpiryDateTime,
        applySearch() {
            this.updateParams({ page: 1 });
            this.paginatedData();
        },
        resetFilters() {
            this.search = '';
            this.updateParams({ page: 1 });
            this.paginatedData();
        },
        async paginatedData() {
            try {
                this.tableDataLoading = true;

                const response = await axios.get(route('admin.pgc-giftcards.index'), {
                    params: {
                        page: this.serverParams.page,
                        per_page: this.serverParams.perPage,
                        search: this.search || undefined,
                        api: true,
                    },
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                });

                this.totalRecords = response.data.total;
                this.paginationOptions.perPage = response.data.per_page;
                this.serverParams.perPage = response.data.per_page;
                this.paginationOptions.setCurrentPage = response.data.current_page;
                this.allSeries = response.data.data;
            } catch (e) {
                console.log(e);
            } finally {
                this.tableDataLoading = false;
            }
        },
        async deleteSeries(seriesSlug) {
            await this.$inertia.delete(route('admin.pgc-giftcards.delete', seriesSlug), {
                preserveScroll: true,
                onSuccess: async () => {
                    await this.paginatedData();
                },
            });
        },
        async confirmDeleteSeries(seriesSlug) {
            const confirmed = await this.deleteAlert(
                'Are you sure you want to delete this series?',
                'All gift cards in this series will be removed.',
            );

            if (confirmed) {
                await this.deleteSeries(seriesSlug);
            }
        },
    },
};
</script>
